export interface Token {
  base_quote: string;
  base_ccy: string;
  quote_ccy: string;
  exchange_symbol: string;
  instrument_id: number;
  exchange: string;
  tenors?: string[];
}

const normalize = (value?: string) => (value || '').trim().toUpperCase();

export const isDeribitExchange = (exchange?: string) => normalize(exchange) === 'DERIBIT';

export const getTokenExchangeSymbol = (token: Pick<Token, 'exchange_symbol'>) => {
  return token.exchange_symbol;
};

export const getTokenBaseQuoteSymbol = (token: Pick<Token, 'base_quote'>) => {
  return token.base_quote;
};

export const getTokenCandlestickSymbol = (
  token: Pick<Token, 'base_quote' | 'exchange_symbol' | 'exchange'>,
) => {
  return isDeribitExchange(token.exchange) ? token.base_quote : token.exchange_symbol;
};

export const getTokenRequestSymbol = getTokenCandlestickSymbol;

export const getTokenDisplaySymbol = (token: Pick<Token, 'base_ccy'>) => token.base_ccy;

export const matchesTokenSymbolByExchange = (
  token: Pick<Token, 'base_quote' | 'exchange_symbol' | 'exchange'>,
  symbol: string,
) => {
  const normalizedSymbol = normalize(symbol);
  return (
    normalize(token.base_quote) === normalizedSymbol ||
    normalize(token.exchange_symbol) === normalizedSymbol
  );
};

const findTokenBySymbolAndExchange = (rawSymbol: string, exchange: string, tokens: Token[]) => {
  const normalizedRawSymbol = normalize(rawSymbol);
  const normalizedExchange = normalize(exchange);

  return tokens.find(
    (token) =>
      normalize(token.exchange) === normalizedExchange &&
      matchesTokenSymbolByExchange(token, normalizedRawSymbol),
  );
};

export const resolveCandlestickSymbolByExchange = (
  rawSymbol: string,
  exchange: string,
  tokens: Token[],
): string => {
  const matchedToken = findTokenBySymbolAndExchange(rawSymbol, exchange, tokens);
  return matchedToken ? getTokenCandlestickSymbol(matchedToken) : rawSymbol;
};

export const resolveExchangeSymbolByExchange = (
  rawSymbol: string,
  exchange: string,
  tokens: Token[],
): string => {
  const matchedToken = findTokenBySymbolAndExchange(rawSymbol, exchange, tokens);
  return matchedToken ? getTokenExchangeSymbol(matchedToken) : rawSymbol;
};

export const resolveBaseQuoteSymbolByExchange = (
  rawSymbol: string,
  exchange: string,
  tokens: Token[],
): string => {
  const matchedToken = findTokenBySymbolAndExchange(rawSymbol, exchange, tokens);
  return matchedToken ? getTokenBaseQuoteSymbol(matchedToken) : rawSymbol;
};

export const resolveRequestSymbolByExchange = (
  rawSymbol: string,
  exchange: string,
  tokens: Token[],
): string => {
  return resolveCandlestickSymbolByExchange(rawSymbol, exchange, tokens);
};
